import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { fetchGolferDetail } from "@/lib/contestApi";

const Ctx = createContext({ openGolfer: () => {}, enabled: false });
export const useGolferDetail = () => useContext(Ctx);

const RESULT_STYLE = {
  condor: "bg-pbo-gold text-pbo-ink",
  albatross: "bg-pbo-gold text-pbo-ink",
  eagle: "bg-pbo-gold/80 text-pbo-ink",
  birdie: "bg-pbo-green text-pbo-cream",
  par: "bg-pbo-paper text-pbo-ink border border-pbo-border",
  bogey: "bg-stone-200 text-pbo-ink",
  double: "bg-red-100 text-red-700",
  triple: "bg-red-200 text-red-800",
  worse: "bg-red-300 text-red-900",
};
const RESULT_ORDER = ["condor", "albatross", "eagle", "birdie", "par", "bogey", "double", "triple", "worse"];
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : w.endsWith("y") ? "" : "s"}`;
const label = (r, n) => {
  const names = { par: "pars", birdie: "birdies", bogey: "bogeys", eagle: "eagles", double: "doubles", triple: "triples", worse: "worse", condor: "condors", albatross: "albatrosses" };
  if (n === 1) return `1 ${r === "worse" ? "worse" : r}`;
  return `${n} ${names[r] || r}`;
};

const HoleChip = ({ h }) => {
  const played = h.strokes != null;
  const cls = played ? RESULT_STYLE[h.result] || RESULT_STYLE.par : "bg-white text-pbo-muted/50 border border-dashed border-pbo-border";
  const diff = played ? h.strokes - (h.personal_par ?? h.par) : null;
  return (
    <div
      className={`rounded-md px-1 py-1 text-center ${cls}`}
      title={played ? `Hole ${h.hole} · par ${h.par} (yours ${h.personal_par}) · ${h.strokes} strokes · ${h.result} · ${h.points > 0 ? "+" : ""}${h.points} pts` : `Hole ${h.hole} · par ${h.par} · not played`}
      data-testid={`hole-chip-${h.hole}`}
    >
      <div className="text-[9px] leading-none opacity-70">{h.hole}</div>
      <div className="font-mono font-bold text-sm leading-tight">{played ? h.strokes : "·"}</div>
      <div className="text-[9px] leading-none font-semibold">
        {played ? (diff === 0 ? "E" : diff > 0 ? `+${diff}` : diff) : `p${h.par}`}
      </div>
    </div>
  );
};

const RoundBlock = ({ r }) => {
  const counts = RESULT_ORDER.filter((k) => (r.results?.[k] || 0) > 0);
  return (
    <div className="rounded-xl border border-pbo-border bg-white p-3" data-testid={`detail-round-${r.round_number}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <span className="font-serif text-pbo-green text-lg">R{r.round_number}</span>
          <span className="text-xs text-pbo-muted ml-2">
            {r.course_name || ""}
            {r.handicap != null ? ` · hcp ${r.handicap}` : ""}
            {r.finished ? " · closed" : r.holes_played ? ` · thru ${r.holes_played}` : " · not started"}
          </span>
        </div>
        <div className="text-right">
          <span className={`font-mono font-bold text-lg ${r.points > 0 ? "text-pbo-green-soft" : r.points < 0 ? "text-red-600" : "text-pbo-ink"}`}>
            {r.points == null ? "—" : r.points > 0 ? `+${r.points}` : r.points}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-pbo-muted ml-1">pts</span>
          {r.gross != null && (
            <div className="text-[10px] text-pbo-muted">
              gross {r.gross}{r.net != null && r.net !== r.gross ? ` · net ${r.net}` : ""}
            </div>
          )}
        </div>
      </div>
      {r.holes?.length ? (
        <div className="grid grid-cols-9 gap-1 mt-3">
          {r.holes.map((h) => (
            <HoleChip key={h.hole} h={h} />
          ))}
        </div>
      ) : (
        <div className="text-xs text-pbo-muted mt-2">No hole scores posted yet.</div>
      )}
      {counts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {counts.map((k) => (
            <span key={k} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${RESULT_STYLE[k]}`}>
              {label(k, r.results[k])}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const GolferDetailSheet = ({ code, golfer, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!golfer) return;
    setData(null);
    setError("");
    fetchGolferDetail(code, golfer.id)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load scorecard"));
  }, [code, golfer]);
  const total = data?.total;
  return (
    <Sheet open={!!golfer} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl bg-pbo-cream border-pbo-border" data-testid="golfer-detail-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-serif text-pbo-green text-2xl flex items-baseline gap-3 flex-wrap">
            <span data-testid="golfer-detail-name">{golfer?.name}</span>
            {data && (
              <span className="font-mono text-lg text-pbo-ink">
                {total == null ? "—" : total} <span className="text-[10px] uppercase tracking-wider text-pbo-muted">pts</span>
                {data.bonus > 0 && (
                  <span className="ml-2 text-xs font-semibold bg-pbo-gold/20 rounded px-1.5 py-0.5">bonus +{data.bonus}</span>
                )}
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-pbo-muted text-xs">
            Hole-by-hole from PBO · strokes vs your personal par · tap a hole for details
            {data?.place ? ` · currently ${data.place}${["st", "nd", "rd"][data.place - 1] || "th"} in the tournament` : ""}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3 pb-4">
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-pbo-muted py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading scorecard…
            </div>
          )}
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          {data && data.rounds.length === 0 && (
            <div className="text-sm text-pbo-muted text-center py-6" data-testid="golfer-detail-empty">
              {data.scoring_source === "pbo"
                ? "No rounds scored yet — check back once play starts."
                : "Hole-by-hole detail is available for PBO-scored contests."}
            </div>
          )}
          {data?.rounds.map((r) => (
            <RoundBlock key={r.round_number} r={r} />
          ))}
          {data?.points_table && (
            <div className="text-[10px] text-pbo-muted leading-relaxed">
              Points per hole vs personal par:{" "}
              {Object.entries(data.points_table)
                .map(([k, v]) => `${k} ${v > 0 ? "+" : ""}${v}`)
                .join(" · ")}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const GolferDetailProvider = ({ code, enabled, children }) => {
  const [golfer, setGolfer] = useState(null);
  const openGolfer = useCallback((g) => enabled && g && setGolfer({ id: g.id || g.golfer_id, name: g.name }), [enabled]);
  const value = useMemo(() => ({ openGolfer, enabled }), [openGolfer, enabled]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <GolferDetailSheet code={code} golfer={golfer} onClose={() => setGolfer(null)} />
    </Ctx.Provider>
  );
};

// Small helper: renders a golfer name that opens the scorecard when enabled.
export const GolferName = ({ g, className = "", testId }) => {
  const { openGolfer, enabled } = useGolferDetail();
  if (!enabled) return <span className={className}>{g.name}</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openGolfer(g);
      }}
      className={`text-left hover:text-pbo-gold underline decoration-dotted decoration-pbo-border underline-offset-2 ${className}`}
      data-testid={testId || `golfer-detail-btn-${g.id || g.golfer_id}`}
    >
      {g.name}
    </button>
  );
};
